import React, { Suspense, useMemo, useState, lazy, memo } from "react";
import { Layout, Menu, Tooltip, Popconfirm, Popover, Spin } from "antd";
import {
  PieChartOutlined,
  UserOutlined,
  LogoutOutlined,
  ProfileOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  AiOutlineProduct,
  AiOutlineWallet,
  AiFillWallet,
} from "react-icons/ai";
import {
  RiCoupon2Line,
  RiMenuFold4Fill,
  RiMenuFoldFill,
  RiMoneyRupeeCircleLine,
} from "react-icons/ri";
import { FiSettings, FiUsers } from "react-icons/fi";
import { MdOutlineQueryBuilder } from "react-icons/md";
import { FcLeave } from "react-icons/fc";
import { FaPaypal } from "react-icons/fa";
import { IoStatsChart } from "react-icons/io5";
import { TbCreditCardPay } from "react-icons/tb";
import { LuBadgeHelp } from "react-icons/lu";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { GetRole } from "../components/GetRole";
import { ImUsers } from "react-icons/im";
import ManageEmployees from "../Pages/ManagePoints";
import { PiCoinsDuotone } from "react-icons/pi";
import { CiCircleInfo } from "react-icons/ci";
import EmployeeOperation from "../Pages/Employee/EmployeeOperation";
import SupportChat from "../Pages/Chat/SupportChat";
import ConversationView from "../Pages/Chat/CoversationView";
import Notice from "../Pages/Notice";
import QueryManagement from "../Pages/QueryManagement";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { info } from "autoprefixer";
import ExpiredServices from "../Pages/Services/ExpiredServices";

// ✅ Lazy-load pages
const Home = lazy(() => import("../Pages/Home"));
const Users = lazy(() => import("../Pages/Users/Users"));
const Profile = lazy(() => import("../Pages/Profile/Profile"));
const Redeem = lazy(() => import("../Pages/Profile/Redeem"));
const Leave = lazy(() => import("../Pages/Profile/Leave"));
const Coupon = lazy(() => import("../Pages/Coupons/Coupon"));
const Agent = lazy(() => import("../Pages/Services/Agent"));
const AllServicesTable = lazy(() => import("../Pages/Services/All_Services"));
const LicenceService = lazy(() => import("../Pages/Services/LicenceService"));
const VehicleService = lazy(() => import("../Pages/Services/VehicleService"));
const TrainingService = lazy(() => import("../Pages/Services/TrainingService"));
const Employees = lazy(() => import("../Pages/Employee/Index"));
const ManagerServices = lazy(() => import("../Pages/Services/ManageService"));
const UserOperations = lazy(() => import("../Pages/Users/UserOperations"));
const AllPayment = lazy(() => import("../Pages/Audit/AllPayment"));
const AllExpense = lazy(() => import("../Pages/Audit/AllExpense"));
const AllBranch = lazy(() => import("../Pages/Audit/AllBranch"));
const Settings = lazy(() => import("../Pages/Settings"));
const LeaveManagent = lazy(() => import("../Pages/LeaveManagement"));

const { Header, Content, Footer, Sider } = Layout;

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  // const [open, setOpen] = useState(false);
  const [openWallet, setWalletOpen] = useState(false);
  // const [notificationOpen, setNotificationOpen] = useState(false);

  // Notifications
  // const [notifications, setNotifications] = useState([
  //   {
  //     id: 1,
  //     title: "Welcome to SMTS Portal",
  //     description: "This advanced portal with many functionalities",
  //     time: "5 minutes ago",
  //     read: false,
  //   },
  //   {
  //     id: 2,
  //     title: "Claim Unlimited Rewards",
  //     description: "Register more user services to earn unlimited rewards",
  //     time: "1 hour ago",
  //     read: false,
  //   },
  //   {
  //     id: 3,
  //     title: "Payment received",
  //     description: "Payment of ₹500 has been processed",
  //     time: "2 hours ago",
  //     read: true,
  //   },
  // ]);

  // const unreadCount = notifications.filter((n) => !n.read).length;

  const empData = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const userInitial = empData?.name?.[0]?.toUpperCase() || "U";
  const points = empData?.points ?? 0;
  const role = GetRole();

  const menuItems = useMemo(() => {
    const primary = "#1677ff";
    const accent = "#52c41a";
    const danger = "#ff4d4f";
    const warn = "#faad14";
    const gray = "#595959";
    const getItem = (label, key, icon, children) => ({
      key,
      icon,
      children,
      label,
    });

    return [
      getItem("Home", "/", <PieChartOutlined style={{ color: primary }} />),
      getItem(
        "Profile",
        "profile",
        <ProfileOutlined style={{ color: primary }} />,
        [
          getItem(
            "My Profile",
            "/profile",
            <UserOutlined style={{ color: gray }} />
          ),

          ...(role !== "admin"
            ? [
                getItem(
                  "Rewards Point",
                  "/rewards",
                  <RiMoneyRupeeCircleLine style={{ color: warn }} />
                ),
                getItem(
                  "Leave Balance",
                  "/leave",
                  <FcLeave style={{ color: danger }} />
                ),
              ]
            : []),
        ]
      ),
      getItem("Users", "/users", <UserOutlined style={{ color: accent }} />),
      ...(role === "admin" || role === "manager" || role === "super_admin"
        ? [
            getItem(
              "Employee",
              "employee",
              <FiUsers style={{ color: primary }} />,
              [
                getItem(
                  "Leaves",
                  "/manage-leaves",
                  <FcLeave style={{ color: "#fcbe49" }} size={16} />
                ),
                ...(role === "admin" || role === "super_admin"
                  ? [
                      getItem(
                        "Notice",
                        "/manage-notice",
                        <CiCircleInfo style={{ color: gray }} size={16} />
                      ),
                      getItem(
                        "Points",
                        "/manage-points",
                        <PiCoinsDuotone
                          style={{ color: "#fcbe49" }}
                          size={16}
                        />
                      ),
                      getItem(
                        "All Employees",
                        "/employees",
                        <FiUsers style={{ color: gray }} />
                      ),
                    ]
                  : []),
              ]
            ),
          ]
        : []),
      getItem(
        "Services",
        "services",
        <AiOutlineProduct style={{ color: warn }} />,
        [
          getItem(
            "All Services",
            "/services",
            <AiOutlineProduct style={{ color: gray }} />
          ),
          getItem(
            "Service Expiry",
            "/expired-services",
            <AiOutlineProduct style={{ color: gray }} />
          ),
          ...(role === "admin" || role === "super_admin"
            ? [
                getItem(
                  "Agent & Payees",
                  "/agents",
                  <ImUsers style={{ color: "purple" }} />
                ),
                getItem(
                  "Manage Service",
                  "/manage-services",
                  <FiSettings style={{ color: gray }} />
                ),
              ]
            : []),
        ]
      ),

      getItem("Audits", "audits", <IoStatsChart style={{ color: primary }} />, [
        ...(role === "admin" || role === "manager" || role !== "employee"
          ? [
              getItem(
                "All Branch",
                "/all-branch",
                <HiOutlineOfficeBuilding style={{ color: info }} />
              ),
            ]
          : []),

        // Visible to everyone
        getItem(
          "All Payments",
          "/all-payments",
          <FaPaypal style={{ color: primary }} />
        ),
        getItem(
          "All Expenses",
          "/all-expense",
          <TbCreditCardPay style={{ color: danger }} />
        ),
      ]),

      getItem(
        "Coupons",
        "/coupons",
        <RiCoupon2Line style={{ color: "#8B5CF6" }} />
      ),
      ...(role === "admin" || role === "super_admin"
        ? [
            getItem(
              "Settings",
              "/settings",
              <FiSettings style={{ color: gray }} />
            ),
          ]
        : []),

      // getItem(
      //   "Help & Support",
      //   "/support",
      //   <LuBadgeHelp style={{ color: "#14B8A6" }} />
      // ),
      ...(role === "admin" || role === "manager"
        ? [
            getItem(
              "Website Query",
              "/website-queries",
              <MdOutlineQueryBuilder style={{ color: "#758c42" }} />
            ),
          ]
        : []),
    ];
  }, [role]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    navigate("/login");
  };

  // const markAllAsRead = () =>
  //   setNotifications(notifications.map((n) => ({ ...n, read: true })));
  // const markAsRead = (id) =>
  //   setNotifications(
  //     notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
  //   );
  // const handleNotificationOpenChange = (newOpen) =>
  //   setNotificationOpen(newOpen);

  // Wallet Popover
  const walletContent = (
    <div className="p-3 text-center min-w-[260px]">
      <div className="bg-gradient-to-br from-amber-400 to-orange-500 w-16 h-16 rounded-full mx-auto flex items-center justify-center shadow-lg mb-3">
        <AiFillWallet className="text-white text-3xl" />
      </div>
      <p className="text-3xl font-bold text-gray-800">
        {points ?? 0}
        <span className="text-sm font-normal text-gray-400 ml-1">pts</span>
      </p>
      <p className="text-xs text-gray-500 mb-3">Available Balance</p>
      <button
        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
        onClick={() => navigate("/rewards")}
      >
        View History
      </button>
    </div>
  );

  // Calendar Popover
  // const calendarContent = (
  //   <div className="p-3 min-w-[300px]">
  //     <div className="flex items-center gap-3 mb-4 border-b pb-2">
  //       <div className="bg-blue-100 p-2 rounded-xl">
  //         <AiOutlineCalendar className="text-blue-600 text-xl" />
  //       </div>
  //       <div>
  //         <p className="font-semibold text-gray-800">Leave Balance</p>
  //         <p className="text-xs text-gray-500">Current Year</p>
  //       </div>
  //     </div>
  //     <div className="grid grid-cols-3 gap-3 mb-4">
  //       <div className="bg-green-100/60 rounded-xl p-3 text-center">
  //         <p className="text-xs text-gray-600 mb-1">Available</p>
  //         <p className="text-xl font-bold text-green-700">0</p>
  //       </div>
  //       <div className="bg-red-100/60 rounded-xl p-3 text-center">
  //         <p className="text-xs text-gray-600 mb-1">Taken</p>
  //         <p className="text-xl font-bold text-red-700">0</p>
  //       </div>
  //       <div className="bg-orange-100/60 rounded-xl p-3 text-center">
  //         <p className="text-xs text-gray-600 mb-1">Pending</p>
  //         <p className="text-xl font-bold text-orange-600">0</p>
  //       </div>
  //     </div>
  //     <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-all">
  //       Apply for Leave
  //     </button>
  //   </div>
  // );

  // // Notification Dropdown
  // const notificationMenu = (
  //   <div
  //     className="bg-white rounded-xl shadow-2xl border border-gray-100"
  //     style={{ width: 380, maxHeight: 500, overflow: "hidden" }}
  //   >
  //     {/* Header */}
  //     <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50">
  //       <div>
  //         <h3 className="font-semibold text-gray-800 text-base">
  //           Notifications
  //         </h3>
  //         <p className="text-xs text-gray-500">
  //           You have {unreadCount} unread notifications
  //         </p>
  //       </div>
  //       {unreadCount > 0 && (
  //         <Button
  //           type="link"
  //           size="small"
  //           onClick={markAllAsRead}
  //           className="text-indigo-600 hover:text-indigo-700 text-xs"
  //         >
  //           Mark all read
  //         </Button>
  //       )}
  //     </div>

  //     {/* Notifications List */}
  //     <div style={{ maxHeight: 400, overflowY: "auto" }}>
  //       {notifications.length > 0 ? (
  //         <List
  //           dataSource={notifications}
  //           renderItem={(item) => (
  //             <List.Item
  //               className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-all border-b border-gray-50 ${
  //                 !item.read ? "bg-indigo-50/30" : ""
  //               }`}
  //               onClick={() => markAsRead(item.id)}
  //             >
  //               <div className="flex gap-3 w-full">
  //                 <div
  //                   className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
  //                     !item.read ? "bg-indigo-500" : "bg-transparent"
  //                   }`}
  //                 />
  //                 <div className="flex-1 min-w-0">
  //                   <p
  //                     className={`text-sm mb-1 ${
  //                       !item.read
  //                         ? "font-semibold text-gray-900"
  //                         : "font-normal text-gray-700"
  //                     }`}
  //                   >
  //                     {item.title}
  //                   </p>
  //                   <p className="text-xs text-gray-500 mb-1 line-clamp-2">
  //                     {item.description}
  //                   </p>
  //                   <p className="text-xs text-gray-400">{item.time}</p>
  //                 </div>
  //               </div>
  //             </List.Item>
  //           )}
  //         />
  //       ) : (
  //         <div className="py-12">
  //           <Empty description="No notifications" />
  //         </div>
  //       )}
  //     </div>

  //     {/* Footer */}
  //     {notifications.length > 0 && (
  //       <div className="px-4 py-3 border-t border-gray-100 text-center">
  //         <Button
  //           type="link"
  //           className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
  //         >
  //           View all notifications
  //         </Button>
  //       </div>
  //     )}
  //   </div>
  // );

  return (
    <Layout className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pr-2">
      {/* SIDEBAR */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        className="!bg-transparent mt-0 ml-1 mb-4"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          height: "100vh",
          zIndex: 1000,
        }}
      >
        <div className="h-full m-2 pt-3 rounded-2xl backdrop-blur-xl bg-white/40 border border-white/20 shadow flex flex-col">
          {/* Logo */}
          <div
            className={`flex items-center ${
              collapsed ? "justify-center" : "gap-3 px-4"
            } transition-all duration-300`}
          >
            <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-white shadow-md ring-1 p-1.5 ring-gray-100">
              <img
                src="https://static.vecteezy.com/system/resources/thumbnails/011/482/839/small/driving-school-logo-template-design-steering-wheel-with-graduation-cap-and-book-icon-free-vector.jpg"
                alt="SMTS Icon"
                className="w-10 h-10 object-contain drop-shadow-md"
              />
              {/* SD */}
            </div>
            {!collapsed && (
              <div className="flex flex-col leading-tight">
                <span className="text-lg font-bold bg-gradient-to-r from-green-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent tracking-wide">
                  SkoolDrive
                </span>
                <span className="text-[11px] font-medium text-gray-600 tracking-widest uppercase">
                  Demo Portal
                </span>
              </div>
            )}
          </div>

          <div className="py-4 flex-1 overflow-y-auto">
            <MemoizedMenu
              items={menuItems}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              navigate={navigate}
              locationPath={location.pathname}
            />
          </div>

          {/* Logout */}
          <div className="mt-auto p-3">
            <Popconfirm
              title="Logout?"
              okText="Yes"
              cancelText="Cancel"
              onConfirm={handleLogout}
            >
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600">
                <LogoutOutlined className="text-lg" />
                {!collapsed && <span className="font-medium">Logout</span>}
              </div>
            </Popconfirm>
          </div>
        </div>
      </Sider>

      {/* MAIN LAYOUT */}
      <Layout
        className="!bg-transparent"
        style={{
          marginLeft: collapsed ? 90 : 238,
          transition: "margin-left 0.3s ease",
          minHeight: "100vh",
        }}
      >
        {/* HEADER (exact same) */}
        <Header
          className="flex m-2 mt-3 mb-0 rounded-2xl backdrop-blur-xl bg-white/60 border border-white/30 shadow-lg"
          style={{
            padding: "10px 24px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            height: 60,
          }}
        >
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-10 h-10 rounded-xl hover:bg-indigo-50 transition-all duration-200 flex items-center justify-center group"
          >
            {collapsed ? (
              <RiMenuFoldFill className="text-xl text-slate-600 group-hover:text-indigo-600 transition-colors" />
            ) : (
              <RiMenuFold4Fill className="text-xl text-slate-600 group-hover:text-indigo-600 transition-colors" />
            )}
          </button>

          <div className="ml-auto flex items-center gap-2">
            {/* Wallet */}
            <Popover
              content={walletContent}
              trigger="click"
              open={openWallet}
              onOpenChange={setWalletOpen}
              placement="bottomRight"
            >
              <button className="p-3 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl text-white shadow-md hover:scale-105 hover:shadow-lg transition-all">
                <AiOutlineWallet className="text-lg" />
              </button>
            </Popover>

            {/* Calendar */}
            {/* <Popover
              content={calendarContent}
              trigger="click"
              open={open}
              onOpenChange={setOpen}
              placement="bottomRight"
            >
              <button className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl text-white shadow-md hover:scale-105 hover:shadow-lg transition-all">
                <AiOutlineCalendar className="text-lg" />
              </button>
            </Popover>

            {/* Notifications */}
            {/* <Dropdown
              dropdownRender={() => notificationMenu}
              trigger={["click"]}
              placement="bottomRight"
              open={notificationOpen}
              onOpenChange={handleNotificationOpenChange}
              zIndex={9999}
            >
              <Badge count={unreadCount} offset={[-3, 3]} size="small">
                <button className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl text-white shadow-md hover:scale-105 hover:shadow-lg transition-all">
                  <BellOutlined className="text-lg" />
                </button>
              </Badge>
            </Dropdown>  */}

            {/* User Avatar */}
            <Tooltip title="Profile">
              <div
                className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold cursor-pointer hover:opacity-90 transition-all ml-2 overflow-hidden"
                onClick={() => navigate("/profile")}
              >
                {empData?.profile_pic ? (
                  <img
                    src={`https://api.smts.org.in/${empData.profile_pic}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  userInitial
                )}
              </div>
            </Tooltip>
          </div>
        </Header>

        {/* CONTENT */}
        <Content className="m-2 bg-transparent">
          <div>
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-[70vh]">
                  <Spin
                    size="large"
                    indicator={<LoadingOutlined spin />}
                    tip={
                      <span className="text-gray-500 font-medium tracking-wide">
                        Loading...
                      </span>
                    }
                  />
                </div>
              }
            >
              <Routes>
                <Route path="/support" element={<SupportChat />} />
                <Route path="/support/:id" element={<ConversationView />} />
                <Route path="/" element={<Home />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/agents" element={<Agent />} />
                <Route path="/manage-points" element={<ManageEmployees />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/all-payments" element={<AllPayment />} />
                <Route path="/all-expense" element={<AllExpense />} />
                <Route path="/all-branch" element={<AllBranch />} />
                <Route path="/rewards" element={<Redeem />} />
                <Route path="/coupons" element={<Coupon />} />
                <Route path="/leave" element={<Leave />} />
                <Route path="/manage-leaves" element={<LeaveManagent />} />
                <Route path="/services" element={<AllServicesTable />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/users" element={<Users />} />
                <Route path="/manage-notice" element={<Notice />} />
                <Route path="/manage-services" element={<ManagerServices />} />
                <Route path="/expired-services" element={<ExpiredServices />} />
                <Route path="/website-queries" element={<QueryManagement />} />
                <Route
                  path="/users/:mode/:userid"
                  element={<UserOperations />}
                />
                <Route
                  path="/employees/:mode/:id"
                  element={<EmployeeOperation />}
                />
                <Route
                  path="/add-services/:mode/licence/:userid"
                  element={<LicenceService />}
                />
                <Route
                  path="/add-services/:mode/vehicle/:userid"
                  element={<VehicleService />}
                />
                <Route
                  path="/add-services/:mode/training/:userid"
                  element={<TrainingService />}
                />
                <Route
                  path="*"
                  element={
                    <div className="text-center py-20 text-gray-600">
                      Page not found
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </div>
        </Content>

        {/* FOOTER */}
        <Footer className="text-center !bg-transparent text-slate-600 text-sm">
          SMTS ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
}

// ✅ Memoized Menu
const MemoizedMenu = memo(
  ({ items, openKeys, onOpenChange, navigate, locationPath }) => (
    <Menu
      mode="inline"
      selectedKeys={[locationPath]}
      openKeys={openKeys}
      onOpenChange={(keys) => {
        const latestOpenKey = keys.find((key) => !openKeys.includes(key));
        onOpenChange(latestOpenKey ? [latestOpenKey] : []);
      }}
      onClick={({ key }) => {
        if (key.startsWith("/")) navigate(key);
      }}
      items={items}
      theme="light"
      className="!bg-transparent !border-none"
      style={{ fontSize: 15 }}
    />
  )
);

export default MainLayout;
